import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Badge } from "@/components/ui/badge";
import { toast } from "sonner";
import { Clock, AlertTriangle } from "lucide-react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Helper: format date as YYYY-MM-DD using LOCAL timezone (not UTC)
const toLocalDateStr = (d) => {
  if (!d) return '';
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
};

const STATUS_OPTIONS = [
  { value: "P", label: "Prezent", color: "bg-green-500", description: "Angajatul a lucrat" },
  { value: "OS", label: "Ore Suplimentare", color: "bg-amber-500", description: "Ore suplimentare (1.5x)" },
  { value: "CO", label: "Concediu Odihnă", color: "bg-orange-400", description: "Concediu de odihnă" },
  { value: "CM", label: "Concediu Medical", color: "bg-red-400", description: "Concediu medical" },
  { value: "CS", label: "Concediu Special", color: "bg-purple-400", description: "Concediu special (nuntă, deces, etc.)" },
  { value: "FS", label: "Fără Salariu", color: "bg-pink-400", description: "Concediu fără plată" },
  { value: "I", label: "Învoire", color: "bg-cyan-400", description: "Învoire/Recuperare" },
  { value: "A", label: "Absent", color: "bg-gray-600", description: "Absență nemotivată" },
  { value: "D", label: "Delegație", color: "bg-blue-400", description: "Delegație/detașare" },
  { value: "-", label: "Resetează", color: "bg-white border-2 border-slate-300", description: "Șterge înregistrarea" },
];

export default function AttendanceEditDialog({ 
  open, 
  onOpenChange, 
  worker, 
  date, 
  currentStatus,
  onSave 
}) {
  const [selectedStatus, setSelectedStatus] = useState("");
  const [hours, setHours] = useState("8");
  const [minutes, setMinutes] = useState("0");
  const [reason, setReason] = useState("");
  const [saving, setSaving] = useState(false);

  // Check if selected date is weekend or holiday
  const isWeekend = date ? (date.getDay() === 0 || date.getDay() === 6) : false;
  const isHoliday = currentStatus?.type === 'holiday';
  const isFreeDay = isWeekend || isHoliday;

  useEffect(() => {
    if (open) {
      // Set default status based on current
      if (currentStatus?.type === 'present') {
        setSelectedStatus("P");
        const h = currentStatus.hours || 8;
        setHours(Math.floor(h).toString());
        setMinutes(Math.round((h % 1) * 60).toString());
      } else if (currentStatus?.type === 'overtime') {
        setSelectedStatus("OS");
        const h = currentStatus.hours || 0;
        setHours(Math.floor(h).toString());
        setMinutes(Math.round((h % 1) * 60).toString());
      } else if (currentStatus?.type && currentStatus.type !== 'no-data' && currentStatus.type !== 'weekend' && currentStatus.type !== 'holiday') {
        const statusMap = {
          'CO': 'CO',
          'CM': 'CM', 
          'CS': 'CS',
          'FS': 'FS',
          'invoire': 'I',
          'absence': 'A',
          'delegate': 'D'
        };
        setSelectedStatus(statusMap[currentStatus.type] || "");
        setHours("8");
        setMinutes("0");
      } else {
        // For weekends/holidays, default to overtime
        if (isFreeDay) {
          setSelectedStatus("OS");
          setHours("0");
          setMinutes("0");
        } else {
          setSelectedStatus("");
          setHours("8");
          setMinutes("0");
        }
      }
      setReason("");
    }
  }, [open, currentStatus, isFreeDay]);

  const handleSave = async () => {
    if (!selectedStatus) {
      toast.error("Selectează un status");
      return;
    }

    // Validate overtime hours
    if (selectedStatus === "OS" && parseFloat(hours) === 0 && parseFloat(minutes) === 0) {
      toast.error("Introduceți numărul de ore suplimentare");
      return;
    }

    setSaving(true);
    try {
      const totalHours = (selectedStatus === "P" || selectedStatus === "OS")
        ? parseFloat(hours) + parseFloat(minutes) / 60 
        : null;

      const response = await fetch(`${API}/attendance/admin-update`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          user_id: worker.id,
          user_name: worker.name,
          date: toLocalDateStr(date),
          status: selectedStatus,
          hours: totalHours,
          minutes: parseInt(minutes) || 0,
          reason: reason,
          is_overtime: selectedStatus === "OS",
          is_free_day: isFreeDay
        })
      });

      if (!response.ok) {
        const error = await response.json();
        throw new Error(error.detail || "Eroare la salvare");
      }

      const result = await response.json();
      toast.success(result.message);
      onSave && onSave();
      onOpenChange(false);
    } catch (error) {
      toast.error(error.message || "Eroare la salvare");
    } finally {
      setSaving(false);
    }
  };

  const formatDate = (d) => {
    return d.toLocaleDateString('ro-RO', { 
      weekday: 'long', 
      day: 'numeric', 
      month: 'long', 
      year: 'numeric' 
    });
  };

  const getDayType = () => {
    if (isHoliday) return { label: "Sărbătoare Legală", color: "bg-yellow-500" };
    if (date?.getDay() === 0) return { label: "Duminică", color: "bg-red-500" };
    if (date?.getDay() === 6) return { label: "Sâmbătă", color: "bg-red-400" };
    return null;
  };

  const dayType = getDayType();

  // Filter status options for free days
  const availableOptions = isFreeDay 
    ? STATUS_OPTIONS.filter(opt => opt.value === "OS" || opt.value === "-")
    : STATUS_OPTIONS;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="text-xl" style={{fontFamily: 'Space Grotesk, sans-serif'}}>
            Editare Pontaj
          </DialogTitle>
          <DialogDescription className="flex items-center gap-2">
            {worker?.name} - {date ? formatDate(date) : ''}
            {dayType && (
              <Badge className={`${dayType.color} text-white ml-2`}>
                {dayType.label}
              </Badge>
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Free Day Alert */}
          {isFreeDay && (
            <Alert className="border-amber-300 bg-amber-50">
              <AlertTriangle className="h-4 w-4 text-amber-600" />
              <AlertDescription className="text-amber-800">
                <strong>Zi liberă:</strong> Orele lucrate în această zi vor fi contorizate ca <strong>ore suplimentare</strong> și plătite cu <strong>150%</strong> din tariful normal.
              </AlertDescription>
            </Alert>
          )}

          {/* Current Status Display */}
          {currentStatus && currentStatus.type !== 'no-data' && currentStatus.type !== 'weekend' && currentStatus.type !== 'holiday' && (
            <div className="p-3 bg-slate-100 rounded-lg">
              <p className="text-sm text-slate-600">Status curent:</p>
              <p className="font-semibold">
                {currentStatus.type === 'present' ? `Prezent - ${currentStatus.hours || 8} ore` :
                 currentStatus.type === 'overtime' ? `Ore Suplimentare - ${currentStatus.hours || 0} ore` :
                 currentStatus.type}
              </p>
            </div>
          )}

          {/* Status Selection */}
          <div>
            <Label className="text-sm font-semibold mb-3 block">
              {isFreeDay ? "Adaugă Ore Suplimentare" : "Selectează Status"}
            </Label>
            <div className={`grid ${isFreeDay ? 'grid-cols-2' : 'grid-cols-3 sm:grid-cols-5'} gap-2`}>
              {availableOptions.map((option) => (
                <button
                  key={option.value}
                  type="button"
                  onClick={() => setSelectedStatus(option.value)}
                  className={`p-3 rounded-lg border-2 transition-all text-center ${
                    selectedStatus === option.value 
                      ? 'border-blue-500 ring-2 ring-blue-200 scale-105' 
                      : 'border-slate-200 hover:border-slate-300'
                  }`}
                >
                  <div className={`w-8 h-8 rounded-full mx-auto mb-2 ${option.color} ${option.value !== '-' ? 'text-white' : ''} flex items-center justify-center`}>
                    {option.value === 'OS' && <Clock className="w-4 h-4" />}
                  </div>
                  <p className="text-xs font-semibold">{option.label}</p>
                </button>
              ))}
            </div>
          </div>

          {/* Hours Input - for Prezent or Ore Suplimentare */}
          {(selectedStatus === "P" || selectedStatus === "OS") && (
            <div className={`p-4 rounded-lg border ${selectedStatus === "OS" ? 'bg-amber-50 border-amber-200' : 'bg-green-50 border-green-200'}`}>
              <Label className={`text-sm font-semibold mb-3 block ${selectedStatus === "OS" ? 'text-amber-800' : 'text-green-800'}`}>
                {selectedStatus === "OS" ? (
                  <span className="flex items-center gap-2">
                    <Clock className="w-4 h-4" />
                    Ore Suplimentare (vor fi plătite 1.5x)
                  </span>
                ) : "Ore Lucrate"}
              </Label>
              <div className="flex items-center gap-4">
                <div className="flex-1">
                  <Label className="text-xs text-slate-600">Ore</Label>
                  <Input
                    type="number"
                    min="0"
                    max="24"
                    value={hours}
                    onChange={(e) => setHours(e.target.value)}
                    className="text-center text-lg font-bold"
                  />
                </div>
                <span className="text-2xl font-bold text-slate-400 pt-5">:</span>
                <div className="flex-1">
                  <Label className="text-xs text-slate-600">Minute</Label>
                  <Input
                    type="number"
                    min="0"
                    max="59"
                    step="15"
                    value={minutes}
                    onChange={(e) => setMinutes(e.target.value)}
                    className="text-center text-lg font-bold"
                  />
                </div>
              </div>
              {selectedStatus === "OS" && parseFloat(hours) > 0 && (
                <div className="mt-3 p-2 bg-amber-100 rounded text-sm text-amber-800">
                  💰 Echivalent: <strong>{((parseFloat(hours) + parseFloat(minutes)/60) * 1.5).toFixed(1)}</strong> ore normale
                </div>
              )}
            </div>
          )}

          {/* Reason Input */}
          <div>
            <Label className="text-sm font-semibold mb-2 block">Motiv / Notă (opțional)</Label>
            <Input
              value={reason}
              onChange={(e) => setReason(e.target.value)}
              placeholder="Ex: Termină proiectul urgent..."
            />
          </div>

          {/* Action Buttons */}
          <div className="flex gap-3">
            <Button
              variant="outline"
              className="flex-1"
              onClick={() => onOpenChange(false)}
            >
              Anulează
            </Button>
            <Button
              className={`flex-1 ${selectedStatus === "OS" ? 'bg-amber-600 hover:bg-amber-700' : 'bg-blue-600 hover:bg-blue-700'}`}
              onClick={handleSave}
              disabled={saving || !selectedStatus}
            >
              {saving ? "Se salvează..." : "Salvează"}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
